import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { useAdminSession } from '@/lib/admin-session';
import { fetchCriterionVersion } from '@/lib/api';
import type { CriterionScoring, CriterionThreshold } from '@matter_extractor/shared';
import { bandLowerBounds, passableBound, plancherBound, scoreBand } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';

/**
 * The SLIDER a threshold is posed with (refonte modulaire §7.2; maquette
 * `docs/specs/maquettes/notation/Objectif.dc.html`, its `renderVals()`).
 *
 * It unfolds UNDER the criterion's row, inside the open objective, and not in a
 * popover - §7.2 says so, and the reason shows as soon as it is drawn: the rail
 * is as wide as the objective and carries a tick per band, which no tooltip
 * would hold. One panel at a time, because one objective is open at a time.
 *
 * ## The rail is the NOTATION, so it is read from the version the tag points at
 *
 * « Le rail dessine les tranches en segments, leurs bornes en graduations, un
 * repère sur le plancher, le curseur se cale sur les débuts de tranche. » Those
 * bands belong to ONE frozen version of the criterion - the one this citation's
 * tag resolves - so the panel reads that version and not the criterion as it
 * stands today. A row citing `production v3` of a criterion whose development
 * scale has moved must offer the bands v3 holds, or the operator would pick a
 * number the session will never be judged against.
 *
 * The read is LAZY, for the reason `criterion-card-popover.tsx` states: an
 * objective cites up to ten criteria, and reading every scale at mount would be
 * ten requests for a panel nobody opened. The quiet pill of the row needs only
 * the plancher, which the card already carries.
 *
 * ## The handle SNAPS, and what is stored is the NUMBER
 *
 * A threshold is « calé sur un début de tranche » (§7.2), so there is nothing
 * between two ticks to land on: the ticks are the control, and the handle is a
 * mark that follows them. What goes into the draft is the band's LOWER BOUND
 * and never its index - « pour qu'une version ultérieure de la notation ne
 * casse rien ». A scale reworked later moves its bands; a threshold of 75 stays
 * a threshold of 75, and lands in whatever band then contains it.
 *
 * « Retirer le seuil » writes `null` and the row falls back to the plancher,
 * which is the criterion's own answer to « where does rempli begin ».
 */

export interface CriterionThresholdSliderProps {
  criterionId: string;
  /** The version this citation's tag points at - whose bands the rail draws. */
  versionId: string;
  criterionName: string;
  /** The threshold posed, or null while the plancher applies. */
  threshold: CriterionThreshold | null;
  onThreshold: (next: CriterionThreshold | null) => void;
}

export function CriterionThresholdSlider({
  criterionId,
  versionId,
  criterionName,
  threshold,
  onThreshold,
}: CriterionThresholdSliderProps) {
  const { email } = useAdminSession();
  // The criterion editor's own key, shape included: `['criterion-version', ...]`
  // holds a `CriterionVersion` on both screens, and the 30 s `staleTime` means
  // whichever read it first is the one the other renders.
  const version = useQuery({
    queryKey: ['criterion-version', email, criterionId, versionId],
    queryFn: () => fetchCriterionVersion(criterionId, versionId),
    retry: false,
  });

  const scoring = version.data?.content.scoring ?? null;

  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.criterionThresholdPanel}
      className="mr-9 mb-1.5 flex flex-col gap-2 rounded-md border-2 border-gray-200 bg-white px-4 pt-3.5 pb-3"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="font-semibold text-gray-900 text-sm">
          Seuil sur « {criterionName} »
          {scoring === null ? null : (
            <span className="font-normal text-gray-600">
              {' '}
              · noté, {scoring.bands.length} tranches · plancher {plancherBound(scoring)}
            </span>
          )}
        </p>
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.criterionThresholdRemove}
          onClick={() => onThreshold(null)}
          disabled={threshold === null}
          className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2 text-caption text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:text-gray-400 disabled:hover:bg-transparent"
        >
          <X aria-hidden="true" className="size-3.5" />
          Retirer le seuil
        </button>
      </div>

      {version.isPending ? <p className="text-gray-600 text-sm">Lecture de la notation…</p> : null}
      {version.isError ? (
        <p className="text-gray-700 text-sm">La notation de ce critère n’a pas pu être lue.</p>
      ) : null}
      {scoring === null ? null : (
        <Rail scoring={scoring} threshold={threshold} onThreshold={onThreshold} />
      )}
    </div>
  );
}

/** One band of the rail, as the drawing needs it: its bounds and its text. */
type Segment = { readonly lower: number; readonly upper: number; readonly text: string };

/**
 * The rail itself: a filled segment from the effective threshold upward, a tick
 * button at every band start, the plancher marked, and the targeted band's
 * description under it.
 *
 * Positions are PERCENTAGES of the 0 to 100 scale a score lives on, which is
 * what makes a band's width its own span - the mockup does the same, and it is
 * the only layout that keeps the ticks under the numbers they name.
 */
function Rail({
  scoring,
  threshold,
  onThreshold,
}: {
  scoring: CriterionScoring;
  threshold: CriterionThreshold | null;
  onThreshold: (next: CriterionThreshold) => void;
}) {
  const lowers = bandLowerBounds(scoring.bands);
  const segments: Segment[] = scoring.bands.map((band, index) => ({
    lower: lowers[index] ?? 0,
    upper: band.upper,
    text: band.text,
  }));
  const plancher = plancherBound(scoring);
  // What the criterion is judged on RIGHT NOW: the threshold when one is posed,
  // the plancher otherwise. The handle sits on it either way, so opening the
  // panel on a row with no threshold shows where « rempli » already begins.
  const effective = threshold ?? plancher;
  /*
    The band the effective bar FALLS IN, by the engine's own rule, and not the
    band whose start equals it.

    A threshold posed here is always a band start, but a STORED one need not be:
    H46 converts an old `> 80` into 81, and a notation reworked since may have
    moved its bands under a number somebody chose (§7.2 stores the number for
    exactly that reason). Matched on equality, those read as « no band » and
    fell back to the first one - the rail then showed nothing filled and
    described the bottom band, under a handle sitting near the top.
  */
  const covering = scoreBand(
    {
      plancher,
      passable: passableBound(scoring),
      bands: segments.map(({ lower, upper }) => ({ lower, upper })),
    },
    effective,
  );
  const current = segments.find((segment) => segment.lower === covering?.lower) ?? segments[0];
  // Filled from the covering band's START: the rail is made of bands, so a bar
  // that lands inside one fills it whole rather than leaving it grey.
  const from = current?.lower ?? effective;

  return (
    <>
      <div className="relative mx-3.5 h-23">
        <div className="absolute top-5 right-0 left-0 h-3 overflow-hidden rounded-pill bg-gray-100">
          {segments.map((segment) => (
            <span
              key={segment.lower}
              style={{
                left: `${segment.lower}%`,
                width: `${segment.upper - segment.lower}%`,
              }}
              className={`absolute top-0 bottom-0 border-white border-r-2 ${
                segment.lower >= from ? 'bg-bleu-nuit' : 'bg-rail'
              }`}
            />
          ))}
        </div>

        {segments.map((segment) => {
          const chosen = segment.lower === from;
          return (
            <button
              key={segment.lower}
              type="button"
              data-testid={PROTOCOL_TEST_IDS.criterionThresholdTick}
              data-band-start={segment.lower}
              aria-label={`Seuil à partir de ${segment.lower}`}
              aria-pressed={chosen}
              // The BAND START, never its index: a later version of the notation
              // moves its bands, and a stored index would silently move the
              // threshold with them (§7.2).
              onClick={() => onThreshold(segment.lower)}
              style={{ left: `${segment.lower}%` }}
              className="-translate-x-1/2 absolute top-2 h-15 w-10 cursor-pointer border-0 bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={`absolute top-0 left-[19px] h-9 w-0.5 ${
                  chosen ? 'bg-bleu-nuit' : 'bg-rail'
                }`}
              />
              <span
                className={`absolute top-10 right-0 left-0 text-center text-xs tabular-nums ${
                  chosen ? 'font-bold text-bleu-nuit' : 'text-gray-600'
                }`}
              >
                {segment.lower}
              </span>
            </button>
          );
        })}

        <span className="absolute top-12 right-0 translate-x-1/2 text-gray-600 text-xs tabular-nums">
          100
        </span>
        <span
          data-testid={PROTOCOL_TEST_IDS.criterionThresholdPlancher}
          style={{ left: `${plancher}%` }}
          className="-translate-x-1/2 absolute top-17 flex flex-col items-center gap-0.5 whitespace-nowrap font-semibold text-[11px] text-bleu-nuit leading-[14px]"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
            <title>plancher</title>
            <path d="M4 0 8 8H0z" fill="currentColor" />
          </svg>
          plancher
        </span>
        <span
          aria-hidden="true"
          style={{ left: `${effective}%` }}
          className="-translate-x-1/2 absolute top-3.5 size-6 rounded-full border-[3px] border-bleu-nuit bg-white shadow-md"
        />
      </div>

      {current === undefined ? null : (
        <p className="text-caption text-gray-700">
          <span className="font-semibold text-gray-900">
            {threshold === null
              ? `Plancher de la notation, ${plancher}`
              : `Rempli à partir de ${threshold}`}{' '}
            · tranche {current.lower} - {current.upper} :
          </span>{' '}
          {current.text}
        </p>
      )}
    </>
  );
}
